/** @import { InferOutput } from "~/util/validate.js"; */
import {
	array,
	boolean,
	literal,
	nullable,
	number,
	object,
	optional,
	record,
	string,
	union,
	unknown,
} from "~/util/validate.js";

export const orgResponse = object({
	id: string,
	name: string,
	owner_id: string,
	enterprise_id: string,
	created_at: string,
	updated_at: string,
});
/** @typedef {InferOutput<typeof orgResponse>} orgResponse */

export const enforcedPoliciesResponse = object({
	clipboard_policy: union(literal("allowed"), literal("blocked"), literal("host_copy_blocked")),
	is_privacy_mode_enforced: boolean,
	is_usb_passthrough_blocked: boolean,
	max_bitrate_mbps: number,
});
/** @typedef {InferOutput<typeof enforcedPoliciesResponse>} enforcedPoliciesResponse */

export const adminUsageResponse = object({
	date: string,
	minutes: record(number),
});
/** @typedef {InferOutput<typeof adminUsageResponse>} adminUsageResponse */

export const adminEnterpriseRolePermissionResponse = object({
	key: string,
	name: string,
	description: string,
	access: string,
});
/** @typedef {InferOutput<typeof adminEnterpriseRolePermissionResponse>} adminEnterpriseRolePermissionResponse */

export const adminEnterpriseRoleResponse = object({
	key: string,
	name: string,
	description: string,
	permissions: array(adminEnterpriseRolePermissionResponse),
});
/** @typedef {InferOutput<typeof adminEnterpriseRoleResponse>} adminEnterpriseRoleResponse */

export const adminEnterpriseRolesResponse = object({
	roles: array(adminEnterpriseRoleResponse),
});
/** @typedef {InferOutput<typeof adminEnterpriseRolesResponse>} adminEnterpriseRolesResponse */

export const adminOrgRolePermissionResponse = object({
	key: string,
	name: string,
	description: string,
	access: string,
});
/** @typedef {InferOutput<typeof adminOrgRolePermissionResponse>} adminOrgRolePermissionResponse */

export const adminOrgRoleResponse = object({
	key: string,
	name: string,
	description: string,
	permissions: array(adminOrgRolePermissionResponse),
});
/** @typedef {InferOutput<typeof adminOrgRoleResponse>} adminOrgRoleResponse */

export const adminOrgRolesResponse = object({
	roles: array(adminOrgRoleResponse),
});
/** @typedef {InferOutput<typeof adminOrgRolesResponse>} adminOrgRolesResponse */

export const adminRolesResponse = object({
	enterprise: adminEnterpriseRolesResponse,
	org: adminOrgRolesResponse,
});
/** @typedef {InferOutput<typeof adminRolesResponse>} adminRolesResponse */

export const adminBillingResponse = object({
	organizations: array(orgResponse),
	usage: array(adminUsageResponse),
});
/** @typedef {InferOutput<typeof adminBillingResponse>} adminBillingResponse */

export const adminInvoiceResponse = object({
	id: string,
	status: string,
	total: number,
	credit_type: string,
	credit_type_id: string,
	start_at: string,
	end_at: string,
	issued_at: string,
});
/** @typedef {InferOutput<typeof adminInvoiceResponse>} adminInvoiceResponse */

export const userRefResponse = object({
	id: string,
	name: string,
	email: string,
});
/** @typedef {InferOutput<typeof userRefResponse>} userRefResponse */

export const orgRefResponse = object({
	id: string,
	name: string,
});
/** @typedef {InferOutput<typeof orgRefResponse>} orgRefResponse */

export const groupRefResponse = object({
	id: string,
	name: string,
});
/** @typedef {InferOutput<typeof groupRefResponse>} groupRefResponse */

export const locationRefResponse = object({
	id: string,
	name: string,
});
/** @typedef {InferOutput<typeof locationRefResponse>} locationRefResponse */

export const machineRefResponse = object({
	id: string,
	name: string,
});
/** @typedef {InferOutput<typeof machineRefResponse>} machineRefResponse */

export const connectionResponse = object({
	id: string,
	user_id: string,
	machine_id: string,
	connected_at: nullable(string),
	last_heartbeat_at: nullable(string),
	disconnected_at: nullable(string),
	created_at: string,
	updated_at: string,
	user: nullable(userRefResponse),
	machine: nullable(machineRefResponse),
	control_type: optional(string),
});
/** @typedef {InferOutput<typeof connectionResponse>} connectionResponse */

export const adminMachineResponse = object({
	id: string,
	name: string,
	hostname: optional(string),
	org_id: string,
	user_id: string,
	group_id: string,
	location_id: string,
	is_online: boolean,
	version: string,
	channel: string,
	created_at: string,
	updated_at: string,
	user: nullable(userRefResponse),
	group: nullable(groupRefResponse),
	location: nullable(locationRefResponse),
	connections: array(connectionResponse),
});
/** @typedef {InferOutput<typeof adminMachineResponse>} adminMachineResponse */

export const adminMemberResponse = object({
	id: string,
	name: string,
	email: string,
	role: string,
	created_at: string,
	updated_at: string,
	machines: array(machineRefResponse),
	groups: array(groupRefResponse),
	machine_connections: array(connectionResponse),
});
/** @typedef {InferOutput<typeof adminMemberResponse>} adminMemberResponse */

export const userRejectionResponse = object({
	user_id: string,
	reason: string,
});
/** @typedef {InferOutput<typeof userRejectionResponse>} userRejectionResponse */

export const assignEnterpriseMembersResponse = object({
	assigned: array(string),
	rejected: array(userRejectionResponse),
});
/** @typedef {InferOutput<typeof assignEnterpriseMembersResponse>} assignEnterpriseMembersResponse */

export const assignEnterpriseOrgMembersResponse = object({
	assigned: array(string),
	rejected: array(userRejectionResponse),
});
/** @typedef {InferOutput<typeof assignEnterpriseOrgMembersResponse>} assignEnterpriseOrgMembersResponse */

export const inviteResponse = object({
	id: string,
	enterprise_id: string,
	email: string,
	sender: optional(userRefResponse),
	is_expired: boolean,
	expires_at: optional(nullable(string)),
	created_at: string,
	updated_at: string,
});
/** @typedef {InferOutput<typeof inviteResponse>} inviteResponse */

export const machineAccessResponse = object({
	id: string,
	accessor_id: string,
	accessor: userRefResponse,
});
/** @typedef {InferOutput<typeof machineAccessResponse>} machineAccessResponse */

export const machineAccessInviteResponse = object({
	id: string,
	is_expired: boolean,
	machine: machineRefResponse,
	sender: userRefResponse,
	receiver: userRefResponse,
	expires_at: string,
});
/** @typedef {InferOutput<typeof machineAccessInviteResponse>} machineAccessInviteResponse */

export const inviteRejectionResponse = object({
	email: string,
	reason: string,
});
/** @typedef {InferOutput<typeof inviteRejectionResponse>} inviteRejectionResponse */

export const bulkEnterpriseInviteResponse = object({
	invited: array(inviteResponse),
	rejected: array(inviteRejectionResponse),
});
/** @typedef {InferOutput<typeof bulkEnterpriseInviteResponse>} bulkEnterpriseInviteResponse */

export const createMachineKeyResponse = object({
	key: string,
});
/** @typedef {InferOutput<typeof createMachineKeyResponse>} createMachineKeyResponse */

export const deleteEnterpriseMembersResponse = object({
	deleted: array(string),
	rejected: array(userRejectionResponse),
});
/** @typedef {InferOutput<typeof deleteEnterpriseMembersResponse>} deleteEnterpriseMembersResponse */

export const enterpriseOrgResponse = object({
	id: string,
	name: string,
	enterprise_id: string,
	created_at: string,
	updated_at: string,
});
/** @typedef {InferOutput<typeof enterpriseOrgResponse>} enterpriseOrgResponse */

export const enterpriseGroupResponse = object({
	id: string,
	org_id: string,
	name: string,
	org_name: string,
});
/** @typedef {InferOutput<typeof enterpriseGroupResponse>} enterpriseGroupResponse */

export const orgRoleRefResponse = object({
	id: string,
	name: string,
	role: string,
});
/** @typedef {InferOutput<typeof orgRoleRefResponse>} orgRoleRefResponse */

export const enterpriseMemberResponse = object({
	id: string,
	name: string,
	email: string,
	role: string,
	orgs: array(orgRoleRefResponse),
	created_at: string,
	updated_at: string,
});
/** @typedef {InferOutput<typeof enterpriseMemberResponse>} enterpriseMemberResponse */

export const groupResponse = object({
	id: string,
	org_id: string,
	name: string,
	description: string,
	member_count: number,
	machine_count: number,
	created_at: string,
	updated_at: string,
});
/** @typedef {InferOutput<typeof groupResponse>} groupResponse */

export const hostResponse = object({
	id: string,
	org_id: optional(string),
	org: optional(nullable(orgRefResponse)),
	owner_id: optional(string),
	owner: optional(nullable(userRefResponse)),
	location_id: string,
	peer_id: string,
	name: string,
	version: string,
	channel: string,
	platform: string,
	os: string,
	is_online: boolean,
	created_at: string,
	updated_at: string,
});
/** @typedef {InferOutput<typeof hostResponse>} hostResponse */

export const inviteLookupResponse = object({
	email: string,
	enterprise_name: string,
	sender_name: string,
	is_sso_enabled: boolean,
	is_expired: boolean,
});
/** @typedef {InferOutput<typeof inviteLookupResponse>} inviteLookupResponse */

export const locationResponse = object({
	id: string,
	org_id: string,
	name: string,
	description: string,
	machine_count: number,
	relay_count: number,
	created_at: string,
	updated_at: string,
});
/** @typedef {InferOutput<typeof locationResponse>} locationResponse */

export const machineKeyResponse = object({
	id: string,
	org_id: string,
	user: nullable(userRefResponse),
	name: string,
	description: string,
	last_used_at: nullable(string),
	created_at: string,
	updated_at: string,
});
/** @typedef {InferOutput<typeof machineKeyResponse>} machineKeyResponse */

export const machineResponse = object({
	id: string,
	org_id: string,
	location_id: string,
	peer_id: string,
	name: string,
	version: string,
	channel: string,
	platform: string,
	os: string,
	is_online: boolean,
	enforced_policies: optional(enforcedPoliciesResponse),
	created_at: string,
	updated_at: string,
});
/** @typedef {InferOutput<typeof machineResponse>} machineResponse */

export const orgMemberResponse = object({
	id: string,
	org_id: string,
	name: string,
	email: string,
	created_at: string,
	updated_at: string,
});
/** @typedef {InferOutput<typeof orgMemberResponse>} orgMemberResponse */

export const passkeyResponse = object({
	id: string,
	name: string,
	key_type: optional(string),
	last_used_at: nullable(string),
	created_at: string,
	updated_at: string,
});
/** @typedef {InferOutput<typeof passkeyResponse>} passkeyResponse */

export const relayKeyResponse = object({
	key: string,
});
/** @typedef {InferOutput<typeof relayKeyResponse>} relayKeyResponse */

export const relayStatsResponse = object({
	version: string,
	client_bits_per_second: number,
	host_bits_per_second: number,
	active_connections: number,
	uptime_seconds: number,
	memory_percent: nullable(number),
	disk_used_percent: nullable(number),
	cpu_busy_percent: nullable(number),
	reported_at: string,
});
/** @typedef {InferOutput<typeof relayStatsResponse>} relayStatsResponse */

export const relayResponse = object({
	id: string,
	org_id: string,
	location_id: string,
	location: locationRefResponse,
	name: string,
	description: string,
	private_address: string,
	private_port: number,
	public_address: string,
	public_port: number,
	is_online: boolean,
	is_maintenance_mode_enabled: boolean,
	stats: nullable(relayStatsResponse),
	created_at: string,
	updated_at: string,
});
/** @typedef {InferOutput<typeof relayResponse>} relayResponse */

export const relayMaintenanceModeResponse = object({
	is_maintenance_mode_enabled: boolean,
});
/** @typedef {InferOutput<typeof relayMaintenanceModeResponse>} relayMaintenanceModeResponse */

export const cardResponse = object({
	brand: string,
	last4: string,
	exp_month: number,
	exp_year: number,
});
/** @typedef {InferOutput<typeof cardResponse>} cardResponse */

export const bankAccountResponse = object({
	bank_name: string,
	last4: string,
});
/** @typedef {InferOutput<typeof bankAccountResponse>} bankAccountResponse */

export const paymentMethodResponse = object({
	id: string,
	type: string,
	is_default: boolean,
	card: optional(nullable(cardResponse)),
	us_bank_account: optional(nullable(bankAccountResponse)),
});
/** @typedef {InferOutput<typeof paymentMethodResponse>} paymentMethodResponse */

export const pendingVerification = object({
	id: string,
	type: string,
	verification_url: string,
	us_bank_account: optional(nullable(bankAccountResponse)),
});
/** @typedef {InferOutput<typeof pendingVerification>} pendingVerification */

export const paymentMethodsResponse = object({
	payment_methods: array(paymentMethodResponse),
	pending_setup_intents: array(pendingVerification),
});
/** @typedef {InferOutput<typeof paymentMethodsResponse>} paymentMethodsResponse */

export const checkoutSessionResponse = object({
	url: string,
});
/** @typedef {InferOutput<typeof checkoutSessionResponse>} checkoutSessionResponse */

export const domainRefResponse = object({
	id: string,
	domain: string,
	state: string,
});
/** @typedef {InferOutput<typeof domainRefResponse>} domainRefResponse */

export const enterpriseAPIKeyResponse = object({
	id: string,
	enterprise_id: string,
	name: string,
	description: string,
	key_hint: string,
	is_expired: boolean,
	creator: optional(nullable(userRefResponse)),
	expires_at: optional(nullable(string)),
	last_used_at: optional(nullable(string)),
	created_at: string,
	updated_at: string,
});
/** @typedef {InferOutput<typeof enterpriseAPIKeyResponse>} enterpriseAPIKeyResponse */

export const createEnterpriseAPIKeyResponse = object({
	key: string,
});
/** @typedef {InferOutput<typeof createEnterpriseAPIKeyResponse>} createEnterpriseAPIKeyResponse */

export const auditActorResponse = object({
	type: string,
	id: string,
	name: string,
	email: optional(string),
});
/** @typedef {InferOutput<typeof auditActorResponse>} auditActorResponse */

export const auditLogResponse = object({
	id: string,
	enterprise_id: string,
	org_id: optional(string),
	action: string,
	request_id: string,
	ip_address: string,
	details: optional(unknown),
	actor: auditActorResponse,
	created_at: string,
	updated_at: string,
});
/** @typedef {InferOutput<typeof auditLogResponse>} auditLogResponse */

export const scimOrgGroupMappingResponse = object({
	id: string,
	group_id: string,
	group_name: string,
});
/** @typedef {InferOutput<typeof scimOrgGroupMappingResponse>} scimOrgGroupMappingResponse */

export const scimOrgMappingResponse = object({
	id: string,
	org_id: string,
	org_name: string,
	role: string,
	groups: array(scimOrgGroupMappingResponse),
});
/** @typedef {InferOutput<typeof scimOrgMappingResponse>} scimOrgMappingResponse */

export const scimGroupResponse = object({
	id: string,
	name: string,
	idp_id: string,
	member_count: number,
	enterprise_role: string,
	mappings: array(scimOrgMappingResponse),
	created_at: string,
	updated_at: string,
});
/** @typedef {InferOutput<typeof scimGroupResponse>} scimGroupResponse */

export const scimRoleVoteResponse = object({
	scim_group_id: string,
	scim_group_name: string,
	role: string,
});
/** @typedef {InferOutput<typeof scimRoleVoteResponse>} scimRoleVoteResponse */

export const scimRoleConflictResponse = object({
	org_id: string,
	org_name: string,
	votes: array(scimRoleVoteResponse),
});
/** @typedef {InferOutput<typeof scimRoleConflictResponse>} scimRoleConflictResponse */

export const scimGroupMemberResponse = object({
	id: string,
	workos_user_id: string,
	user_id: string,
	name: string,
	email: string,
	conflicts: array(scimRoleConflictResponse),
});
/** @typedef {InferOutput<typeof scimGroupMemberResponse>} scimGroupMemberResponse */

export const sessionEnterpriseResponse = object({
	id: string,
	name: string,
	owner_id: string,
	role: string,
	is_sso_enabled: boolean,
	is_scim_enabled: boolean,
	is_tfa_required: boolean,
	is_workos_configured: boolean,
	is_group_sync_enabled: boolean,
	has_verified_domain: boolean,
	has_metronome_customer: optional(boolean),
	created_at: string,
	updated_at: string,
});
/** @typedef {InferOutput<typeof sessionEnterpriseResponse>} sessionEnterpriseResponse */

export const sessionUserResponse = object({
	id: string,
	name: string,
	email: string,
	has_tfa: boolean,
	created_at: string,
	updated_at: string,
});
/** @typedef {InferOutput<typeof sessionUserResponse>} sessionUserResponse */

export const sessionMachineResponse = object({
	id: string,
	name: string,
	org_id: string,
	is_managed: boolean,
	created_at: string,
	updated_at: string,
});
/** @typedef {InferOutput<typeof sessionMachineResponse>} sessionMachineResponse */

export const sessionEnterprisePermissionsResponse = object({
	role: string,
	permissions: record(string),
});
/** @typedef {InferOutput<typeof sessionEnterprisePermissionsResponse>} sessionEnterprisePermissionsResponse */

export const sessionOrgPermissionsResponse = object({
	role: string,
	permissions: record(string),
});
/** @typedef {InferOutput<typeof sessionOrgPermissionsResponse>} sessionOrgPermissionsResponse */

export const sessionPermissionsResponse = object({
	enterprise: sessionEnterprisePermissionsResponse,
	orgs: record(sessionOrgPermissionsResponse),
});
/** @typedef {InferOutput<typeof sessionPermissionsResponse>} sessionPermissionsResponse */

export const sessionInfoResponse = object({
	user: nullable(sessionUserResponse),
	machine: nullable(sessionMachineResponse),
	enterprise: nullable(sessionEnterpriseResponse),
	permissions: optional(sessionPermissionsResponse),
});
/** @typedef {InferOutput<typeof sessionInfoResponse>} sessionInfoResponse */

export const sessionResponse = object({
	token: string,
});
/** @typedef {InferOutput<typeof sessionResponse>} sessionResponse */

export const ssoConfigLinkResponse = object({
	link: string,
});
/** @typedef {InferOutput<typeof ssoConfigLinkResponse>} ssoConfigLinkResponse */

export const ssoProviderRefResponse = object({
	id: string,
	name: string,
});
/** @typedef {InferOutput<typeof ssoProviderRefResponse>} ssoProviderRefResponse */

export const ssoProviderResponse = object({
	id: string,
	enterprise_id: string,
	name: string,
	is_sso_enabled: boolean,
	is_scim_enabled: boolean,
	domains: array(domainRefResponse),
	created_at: string,
	updated_at: string,
});
/** @typedef {InferOutput<typeof ssoProviderResponse>} ssoProviderResponse */

export const stepUpTokenResponse = object({
	token: string,
});
/** @typedef {InferOutput<typeof stepUpTokenResponse>} stepUpTokenResponse */

export const tfaCreateResponse = object({
	tfa_auth_uri: string,
});
/** @typedef {InferOutput<typeof tfaCreateResponse>} tfaCreateResponse */

export const tfaRecoveryCode = object({
	code: string,
	used: boolean,
});
/** @typedef {InferOutput<typeof tfaRecoveryCode>} tfaRecoveryCode */

export const tfaSetupResponse = object({
	token: string,
	tfa_auth_uri: string,
});
/** @typedef {InferOutput<typeof tfaSetupResponse>} tfaSetupResponse */

export const unassignEnterpriseOrgMembersResponse = object({
	unassigned: array(string),
	rejected: array(userRejectionResponse),
});
/** @typedef {InferOutput<typeof unassignEnterpriseOrgMembersResponse>} unassignEnterpriseOrgMembersResponse */

export const webauthnLoginBody = object({
	machine_id: optional(string),
	id: string,
	type: string,
	rawId: string,
	authenticatorAttachment: optional(string),
	clientExtensionResults: optional(record(string)),
	response: object({
		clientDataJSON: string,
		authenticatorData: string,
		signature: string,
		userHandle: optional(string),
	}),
});
/** @typedef {InferOutput<typeof webauthnLoginBody>} webauthnLoginBody */

export const webauthnRegistrationBody = object({
	token: optional(string),
	password: optional(string),
	id: string,
	type: string,
	rawId: string,
	authenticatorAttachment: optional(string),
	clientExtensionResults: optional(record(string)),
	response: object({
		clientDataJSON: string,
		attestationObject: string,
		authenticatorData: optional(string),
		publicKey: optional(string),
		publicKeyAlgorithm: optional(number),
		transports: optional(array(string)),
	}),
});
/** @typedef {InferOutput<typeof webauthnRegistrationBody>} webauthnRegistrationBody */

export const webauthnLoginOptions = object({
	publicKey: object({
		challenge: string,
		timeout: optional(number),
		rpId: optional(string),
		allowCredentials: optional(
			array(
				object({
					type: string,
					id: string,
					transports: optional(array(string)),
				}),
			),
		),
		userVerification: optional(string),
		hints: optional(array(string)),
		extensions: optional(record(string)),
	}),
	mediation: optional(string),
});
/** @typedef {InferOutput<typeof webauthnLoginOptions>} webauthnLoginOptions */

export const webauthnRegistrationOptions = object({
	publicKey: object({
		rp: object({
			id: string,
			name: string,
		}),
		user: object({
			id: string,
			name: string,
			displayName: string,
		}),
		challenge: string,
		pubKeyCredParams: optional(
			array(
				object({
					type: string,
					alg: number,
				}),
			),
		),
		timeout: optional(number),
		excludeCredentials: optional(
			array(
				object({
					type: string,
					id: string,
					transports: optional(array(string)),
				}),
			),
		),
		authenticatorSelection: optional(
			object({
				authenticatorAttachment: optional(string),
				requireResidentKey: optional(nullable(boolean)),
				residentKey: optional(string),
				userVerification: optional(string),
			}),
		),
		hints: optional(array(string)),
		attestation: optional(string),
		attestationFormats: optional(array(string)),
		extensions: optional(record(string)),
	}),
	mediation: optional(string),
});
/** @typedef {InferOutput<typeof webauthnRegistrationOptions>} webauthnRegistrationOptions */
